import { BADGES } from "@/app/components/routes/auth_routes/CartManagement/BillingMirrorComponent/MemberShipComponent/MembershipPlans/PlanCard";

const SavingsTile = ({ label, amount, wide }: { label: string; amount: number; wide?: boolean }) => (
  <div
    className={`flex-1 min-w-px flex items-center bg-[#e3ffed] rounded-[20px] px-[32px] py-[16px] ${
      wide ? "justify-between" : "justify-center"
    }`}
  >
    <div
      className={`flex ${wide ? "flex-1 items-center justify-between" : "flex-col gap-[4px] items-start justify-center"}`}
    >
      <p className="text-[16px] font-medium text-black tracking-[1px] uppercase whitespace-nowrap">{label}</p>
      <p className="text-[22px] font-bold text-[#00360e] whitespace-nowrap">₹{Math.trunc(amount)}</p>
    </div>
  </div>
);

const TABLE_COLUMNS = "grid grid-cols-[minmax(0,1fr)_400px_300px] items-center gap-[16px] px-[24px]";

const CartLineRow = ({ item, last }: { item: any; last: boolean }) => (
  <div
    className={`w-full ${TABLE_COLUMNS} py-[12px] text-[24px] ${last ? "" : "border-b border-solid border-[#e3e8ef]"}`}
  >
    <div className="min-w-0 flex items-center gap-[8px] text-black">
      <p className="truncate font-semibold text-[22px]">{item?.item_name}</p>
      {item?.with_membership_price === 0 && (
        <span className="flex items-center justify-center h-[28px] px-[10px] bg-[#e3ffed] rounded-[10px] text-[#409c18] text-[18px] font-semibold shrink-0">
          Free
        </span>
      )}
    </div>
    <p className="text-center text-black font-monoNums tabular-nums text-[22px]">₹{item?.actual_price ?? 0}</p>
    <p className="font-bold text-[#30820d] text-right font-monoNums tabular-nums text-[22px]">
      ₹{item?.with_membership_price ?? 0}
    </p>
  </div>
);

const TickLine = ({ children }: { children: React.ReactNode }) => (
  <div className="flex items-start gap-[12px]">
    <img src="/membership_tick.svg" alt="" className="w-[20px] h-[20px] shrink-0 mt-[3px]" />
    <p className="text-[18px] font-medium text-black">{children}</p>
  </div>
);

const BenefitSection = ({
  heading,
  divided,
  children,
}: {
  heading: string;
  divided?: boolean;
  children: React.ReactNode;
}) => (
  <div className={`flex flex-col gap-[16px] ${divided ? "pt-[24px] border-t border-solid border-[#e1e8f2]" : ""}`}>
    <p className="text-[16px] font-semibold text-black tracking-[1px] uppercase">{heading}</p>
    <div className="grid grid-cols-2 items-start gap-x-[48px] gap-y-[18px]">{children}</div>
  </div>
);

const PitchingDetailComponent = ({ card, scrollRef }: { card?: any; scrollRef?: React.RefObject<HTMLDivElement> }) => {
  if (!card) return null;

  const benefits = card.benefit_lines ?? [];
  const otherBenefits = card.other_benefit_lines ?? [];
  const cartItems = card.cart_line_items ?? [];
  const cartSavings = card.savings?.savings_on_this_cart;
  const hasCartSavings = cartSavings != null;
  const badge = card.is_top_best_match ? BADGES.BEST_MATCH : /* card.is_recommended ? BADGES.RECOMMENDED : */ null;
  const hasDiscount = card.discount_percentage > 0;

  return (
    <div className="w-full min-h-0 flex flex-col px-[80px] py-[35px] font-roboto leading-[normal]">
      <div className="flex items-center justify-between gap-[16px] shrink-0">
        <h2 className="text-[32px] font-semibold text-black whitespace-nowrap">{card.category} Package</h2>
        {badge && (
          <span
            className={`relative flex items-center py-[5px] rounded-[5px] text-[16.8px] font-semibold whitespace-nowrap ${badge.className}`}
          >
            {badge.icon && (
              <img src={badge.icon} alt="" className="absolute left-[-11px] top-[-10px] w-[45px] h-[45px]" />
            )}
            {badge.label}
          </span>
        )}
      </div>

      <div className="flex-1 min-h-0 flex flex-col gap-[32px] py-[32px]">
        <div className="flex gap-[60px] shrink-0">
          <SavingsTile label="membership SAVINGS" amount={card.savings?.membership_savings} wide={!hasCartSavings} />
          {hasCartSavings && <SavingsTile label="SAVE IN THIS CART" amount={cartSavings} />}
        </div>

        <div
          ref={scrollRef}
          data-mirror-membership-detail-scroll="true"
          className="flex-1 min-h-0 flex flex-col gap-[32px] overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {cartItems.length > 0 && (
            <div className="shrink-0 bg-white border border-solid border-[#e3e8ef] rounded-[20px] flex flex-col">
              <div
                className={`${TABLE_COLUMNS} bg-[#f7f5f2] py-[10px] rounded-tl-[20px] rounded-tr-[20px] text-[20px] text-black`}
              >
                <p className="truncate text-[20px]">ITEM</p>
                <p className="text-center text-[20px]">ACTUAL PRICE</p>
                <p className="text-right text-[20px]">WITH MEMBERSHIP</p>
              </div>
              <div className="flex flex-col gap-[10px] py-[12px]">
                {cartItems.map((item: any, index: number) => (
                  <CartLineRow key={index} item={item} last={index === cartItems.length - 1} />
                ))}
              </div>
            </div>
          )}

          {(benefits.length > 0 || otherBenefits.length > 0) && (
            <div className="shrink-0 flex flex-col gap-[24px] bg-[#f9fafb] border border-solid border-[#e1e8f2] rounded-[20px] pt-[16px] pb-[24px] px-[24px]">
              {benefits.length > 0 && (
                <BenefitSection heading="Benefits">
                  {benefits.map((line: any, index: number) => (
                    <TickLine key={index}>{typeof line === "string" ? line : line?.benefit_name}</TickLine>
                  ))}
                </BenefitSection>
              )}
              {otherBenefits.length > 0 && (
                <BenefitSection heading="Other Benefits" divided={benefits.length > 0}>
                  {otherBenefits.map((line: any, index: number) => (
                    <TickLine key={index}>{line?.text}</TickLine>
                  ))}
                </BenefitSection>
              )}
            </div>
          )}
        </div>
      </div>

      {card.price_per_year > 0 && (
        <div className="w-full shrink-0 flex items-center justify-between gap-[16px] bg-gradient-to-r from-[#e6ffdc] to-white border border-solid border-[#409c18] rounded-[20px] p-[24px]">
          <p className="text-[20px] font-medium text-black whitespace-nowrap">{card.category} Care for 12 months</p>
          <div className="flex items-end gap-[10px] text-black whitespace-nowrap">
            <p className="text-[24px] font-bold">₹{card.price_per_year_after_discount}</p>
            {hasDiscount && (
              <>
                <p className="text-[20px] line-through">₹{card.price_per_year}</p>
                <p className="text-[20px] font-bold text-[#409c18]">Get {card.discount_percentage}% off</p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PitchingDetailComponent;
